import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Storage as StorageIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import api from '../services/api';

/**
 * Storage Analytics Dashboard
 * Shows live storage usage per company with breakdowns and trends
 */
const StorageAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('30d');

  useEffect(() => {
    fetchStorageAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange]);

  const fetchStorageAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/companies/storage-analytics?range=${timeRange}`);
      if (response.data.success) {
        setAnalytics(response.data);
      } else {
        setError('Failed to fetch storage analytics');
      }
    } catch (err) {
      console.error('Error fetching storage analytics:', err);
      setError(err.message || 'Failed to fetch storage analytics');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatGB = (gb) => {
    if (!gb || gb === 0) return '0.00 GB';
    return `${gb.toFixed(2)} GB`;
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 95) return 'error';
    if (percentage >= 80) return 'warning';
    return 'success';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  const { summary, topConsumers, tierDistribution } = analytics || {};

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      {/* Time Range Filter */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}>
        <ToggleButtonGroup
          value={timeRange}
          exclusive
          onChange={(e, newValue) => newValue && setTimeRange(newValue)}
          size="small"
        >
          <ToggleButton value="7d">7 Days</ToggleButton>
          <ToggleButton value="30d">30 Days</ToggleButton>
          <ToggleButton value="90d">90 Days</ToggleButton>
          <ToggleButton value="1y">1 Year</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography color="textSecondary" variant="body2" gutterBottom>
                    Total Storage Used
                  </Typography>
                  <Typography variant="h4">
                    {formatGB(summary?.totalStorageUsedGB || 0)}
                  </Typography>
                </Box>
                <StorageIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
              <Box sx={{ mt: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={Math.min((summary?.usagePercentage || 0), 100)}
                  color={getUsageColor(summary?.usagePercentage || 0)}
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography variant="caption" color="textSecondary">
                  {(summary?.usagePercentage || 0).toFixed(1)}% of total capacity
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography color="textSecondary" variant="body2" gutterBottom>
                    Companies Using Storage
                  </Typography>
                  <Typography variant="h4">
                    {summary?.companiesWithStorage || 0}
                  </Typography>
                </Box>
                <BusinessIcon color="info" sx={{ fontSize: 40 }} />
              </Box>
              <Typography variant="caption" color="textSecondary">
                of {summary?.totalCompanies || 0} total companies
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography color="textSecondary" variant="body2" gutterBottom>
                    Average per Company
                  </Typography>
                  <Typography variant="h4">
                    {formatGB(summary?.averageStorageGB || 0)}
                  </Typography>
                </Box>
                <TrendingUpIcon color="success" sx={{ fontSize: 40 }} />
              </Box>
              <Typography variant="caption" color="textSecondary">
                Across all active companies
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography color="textSecondary" variant="body2" gutterBottom>
                    High Usage Alerts
                  </Typography>
                  <Typography variant="h4" color={summary?.highUsageCount > 0 ? 'error.main' : 'success.main'}>
                    {summary?.highUsageCount || 0}
                  </Typography>
                </Box>
                <WarningIcon color={summary?.highUsageCount > 0 ? 'error' : 'success'} sx={{ fontSize: 40 }} />
              </Box>
              <Typography variant="caption" color="textSecondary">
                Companies above 80% storage
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Storage by Tier */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Storage by Subscription Tier
              </Typography>
              {tierDistribution ? (
                <Box>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2">Free Tier</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {formatGB(tierDistribution.free?.totalStorageGB || 0)}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={tierDistribution.free?.percentageOfTotal || 0}
                      color="inherit"
                      sx={{ height: 8, borderRadius: 4, bgcolor: 'grey.200' }}
                    />
                    <Typography variant="caption" color="textSecondary">
                      {tierDistribution.free?.companyCount || 0} companies
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2">Professional</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {formatGB(tierDistribution.professional?.totalStorageGB || 0)}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={tierDistribution.professional?.percentageOfTotal || 0}
                      color="primary"
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                    <Typography variant="caption" color="textSecondary">
                      {tierDistribution.professional?.companyCount || 0} companies
                    </Typography>
                  </Box>

                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2">Enterprise</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {formatGB(tierDistribution.enterprise?.totalStorageGB || 0)}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={tierDistribution.enterprise?.percentageOfTotal || 0}
                      color="secondary"
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                    <Typography variant="caption" color="textSecondary">
                      {tierDistribution.enterprise?.companyCount || 0} companies
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Typography color="textSecondary">No tier data available</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Top Storage Consumers */}
        <Grid item xs={12} md={8}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Storage Consumers
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Rank</TableCell>
                      <TableCell>Company</TableCell>
                      <TableCell>Tier</TableCell>
                      <TableCell align="right">Storage Used</TableCell>
                      <TableCell align="right">% of Total</TableCell>
                      <TableCell>Usage</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topConsumers && topConsumers.length > 0 ? (
                      topConsumers.map((company, index) => (
                        <TableRow key={company.companyId || index} hover>
                          <TableCell>
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              color={index < 3 ? 'primary.main' : 'textPrimary'}
                            >
                              #{index + 1}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {company.companyName || 'Unknown'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={company.subscriptionTier || 'Free'}
                              size="small"
                              color={
                                company.subscriptionTier === 'ENTERPRISE' ? 'secondary' :
                                company.subscriptionTier === 'PROFESSIONAL' ? 'primary' : 'default'
                              }
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {formatGB(company.storageUsedGB || 0)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="textSecondary">
                              {(company.percentageOfTotal || 0).toFixed(1)}%
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ minWidth: 120 }}>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min((company.percentageOfTotal || 0), 100)}
                              color={getUsageColor(company.percentageOfTotal || 0)}
                              sx={{ height: 6, borderRadius: 3 }}
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography color="textSecondary">No storage data available</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Pricing Info */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Storage Pricing Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Free Storage Allowances by Tier
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Chip label="Free: 100 MB" variant="outlined" />
                <Chip label="Professional: 1 GB" color="primary" variant="outlined" />
                <Chip label="Enterprise: 10 GB" color="secondary" variant="outlined" />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Tiered Pricing (beyond free allowance)
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label="0-10 GB: $0.50/GB" size="small" />
                <Chip label="10-50 GB: $0.45/GB" size="small" />
                <Chip label="50-100 GB: $0.40/GB" size="small" />
                <Chip label="100+ GB: $0.35/GB" size="small" />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default StorageAnalytics;
